import { z } from 'zod';
import { Uuid, Iso, Cents, RoiGrade } from './primitives';

// ─────────────────────────────────────────────────────────
// Enums — mirror the pgEnums in apps/api/drizzle/schema.ts
// ─────────────────────────────────────────────────────────

export const SupplierFileKind = z.enum(['pricing_sheet', 'application', 'contract', 'other']);
export type SupplierFileKind = z.infer<typeof SupplierFileKind>;

export const PricingSheetStatus = z.enum(['parsing', 'parsed', 'parse_failed', 'reviewed']);
export type PricingSheetStatus = z.infer<typeof PricingSheetStatus>;

export const MatchConfidence = z.enum([
  'exact_upc',
  'exact_asin',
  'fuzzy_title_high',
  'fuzzy_title_medium',
  'manual',
  'unmatched',
]);
export type MatchConfidence = z.infer<typeof MatchConfidence>;

export const PricingRowDecision = z.enum(['pending', 'approved', 'skipped']);
export type PricingRowDecision = z.infer<typeof PricingRowDecision>;

// ─────────────────────────────────────────────────────────
// Column map — what the AI / heuristic produced at parse time
// ─────────────────────────────────────────────────────────

export const PricingSheetColumnMap = z.object({
  asin: z.string().nullable().optional(),
  upc: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  brand: z.string().nullable().optional(),
  cost: z.string().nullable().optional(),
  moq: z.string().nullable().optional(),
  mapVersion: z.string(),
  mapStrategy: z.enum(['heuristic', 'llm', 'mixed']),
});
export type PricingSheetColumnMap = z.infer<typeof PricingSheetColumnMap>;

// ─────────────────────────────────────────────────────────
// Domain models
// ─────────────────────────────────────────────────────────

export const PricingSheet = z.object({
  id: Uuid,
  supplierId: Uuid,
  sourceFileId: Uuid,
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  status: PricingSheetStatus,
  parseError: z.string().nullable(),
  columnMap: PricingSheetColumnMap.nullable(),
  totalRows: z.number().int().nonnegative(),
  matchedRows: z.number().int().nonnegative(),
  approvedRows: z.number().int().nonnegative(),
  parsedAt: Iso.nullable(),
  reviewedAt: Iso.nullable(),
  llmCalls: z.number().int().nonnegative(),
  llmInputTokens: z.number().int().nonnegative(),
  llmOutputTokens: z.number().int().nonnegative(),
  promptVersion: z.string().nullable(),
  llmModel: z.string().nullable(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type PricingSheet = z.infer<typeof PricingSheet>;

export const PricingSheetRow = z.object({
  id: Uuid,
  pricingSheetId: Uuid,
  rowIndex: z.number().int().nonnegative(),
  rawRow: z.record(z.unknown()),
  asin: z.string().nullable(),
  upc: z.string().nullable(),
  title: z.string().nullable(),
  brand: z.string().nullable(),
  supplierPriceCents: Cents.nullable(),
  moq: z.number().int().nonnegative().nullable(),
  matchedProductId: Uuid.nullable(),
  matchConfidence: MatchConfidence,
  matchScore: z.number().min(0).max(1).nullable(),
  feeProfileId: Uuid.nullable(),
  feeProfileVersion: z.number().int().nullable(),
  estimatedRoiPct: z.number().nullable(),
  roiGrade: RoiGrade.nullable(),
  decision: PricingRowDecision,
  decidedAt: Iso.nullable(),
  decidedBy: Uuid.nullable(),
  createdAt: Iso,
});
export type PricingSheetRow = z.infer<typeof PricingSheetRow>;

// ─────────────────────────────────────────────────────────
// API — POST /api/pricing-sheets (multipart upload)
// ─────────────────────────────────────────────────────────

/**
 * Upload response — slim shape since the row's terminal state is unknown
 * at POST time (the cron sweep flips it). The SPA polls or subscribes to
 * /events for the rest.
 */
export const CreatePricingSheetResponse = z.object({
  id: Uuid,
  supplierId: Uuid,
  status: PricingSheetStatus,
  filename: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  mimeType: z.string(),
  createdAt: Iso,
});
export type CreatePricingSheetResponse = z.infer<typeof CreatePricingSheetResponse>;

// ─────────────────────────────────────────────────────────
// API — GET /api/pricing-sheets and detail
// ─────────────────────────────────────────────────────────

export const ListPricingSheetsQuery = z.object({
  supplierId: Uuid.optional(),
  status: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListPricingSheetsQuery = z.infer<typeof ListPricingSheetsQuery>;

export const ListPricingSheetsResponse = z.array(PricingSheet);
export type ListPricingSheetsResponse = z.infer<typeof ListPricingSheetsResponse>;

export const PricingSheetDetailResponse = PricingSheet;
export type PricingSheetDetailResponse = z.infer<typeof PricingSheetDetailResponse>;

// ─────────────────────────────────────────────────────────
// API — rows endpoints (Pass 3 + Pass 4)
// ─────────────────────────────────────────────────────────

export const ListPricingSheetRowsQuery = z.object({
  /** Comma-separated grades (e.g. "A,B"). */
  grade: z.string().optional(),
  /** Comma-separated decisions. */
  decision: z.string().optional(),
  /** Comma-separated match confidences. */
  match: z.string().optional(),
  /** Free-text substring match on title (case-insensitive). */
  q: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListPricingSheetRowsQuery = z.infer<typeof ListPricingSheetRowsQuery>;

export const ListPricingSheetRowsResponse = z.object({
  items: z.array(PricingSheetRow),
  total: z.number().int().nonnegative(),
});
export type ListPricingSheetRowsResponse = z.infer<typeof ListPricingSheetRowsResponse>;

export const UpdatePricingSheetRowRequest = z.object({
  decision: PricingRowDecision,
});
export type UpdatePricingSheetRowRequest = z.infer<typeof UpdatePricingSheetRowRequest>;

export const BulkDecideRowsRequest = z.object({
  rowIds: z.array(Uuid).min(1).max(2000),
  decision: PricingRowDecision,
});
export type BulkDecideRowsRequest = z.infer<typeof BulkDecideRowsRequest>;

export const BulkDecideRowsResponse = z.object({
  updated: z.number().int().nonnegative(),
  decision: PricingRowDecision,
});
export type BulkDecideRowsResponse = z.infer<typeof BulkDecideRowsResponse>;

/**
 * POST /api/pricing-sheets/:id/rows/:rowId/match — operator manually links a
 * row to a product when the matcher returned `unmatched` or low confidence.
 */
export const ManualMatchRowRequest = z
  .object({
    productId: Uuid.optional(),
    asin: z.string().length(10).optional(),
  })
  .refine((v) => v.productId !== undefined || v.asin !== undefined, {
    message: 'Either productId or asin is required',
  });
export type ManualMatchRowRequest = z.infer<typeof ManualMatchRowRequest>;

// ─────────────────────────────────────────────────────────
// PATCH /api/pricing-sheets/:id — mark reviewed
// ─────────────────────────────────────────────────────────

export const UpdatePricingSheetRequest = z.object({
  status: z.enum(['reviewed']).optional(),
});
export type UpdatePricingSheetRequest = z.infer<typeof UpdatePricingSheetRequest>;
